import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { JwtAuthService } from './auth/jwt-auth.service';
import {
  CrmBoard, CrmFunil, CrmFunilEtapa, CrmLancamentoResumo, CrmMotivoPerda, CrmOportunidade, CrmOportunidadeHist,
  CrmCorrecaoVinculos, CrmParametro,
  CrmResumo
} from '../interfaces/crm';

/**
 * API do funil de vendas.
 */
@Injectable({ providedIn: 'root' })
export class CrmService {

  readonly path = '/crm';

  constructor(private http: HttpClient, private jwtAuthService: JwtAuthService) { }

  private get url(): string {
    return this.jwtAuthService.apiUrl + this.path;
  }

  funis(): Observable<CrmFunil[]> {
    return this.http.get<CrmFunil[]>(`${this.url}/funil`);
  }

  /** Cria o funil inicial da filial. Idempotente. */
  instalarPadrao(): Observable<CrmFunil> {
    return this.http.post<CrmFunil>(`${this.url}/funil/instalar-padrao`, {});
  }

  /**
   * O quadro inteiro numa chamada: colunas, cartões e totais.
   *
   * @param filtro 'meus' (a API usa o login da sessão), 'sem' (cartões sem responsável) ou um login
   */
  board(funilId: number, cartoes?: number, filtro?: string, busca?: string): Observable<CrmBoard> {
    const params = this.filtros(filtro, busca);
    if (cartoes) {
      params.push(`cartoes=${cartoes}`);
    }
    const qs = params.length ? '?' + params.join('&') : '';
    return this.http.get<CrmBoard>(`${this.url}/funil/${funilId}/board${qs}`);
  }

  /** Próxima página de uma coluna ("carregar mais"), com os mesmos filtros do quadro. */
  cartoesDaEtapa(etapaId: number, pagina: number, filtro?: string, busca?: string): Observable<CrmOportunidade[]> {
    const params = this.filtros(filtro, busca);
    params.push(`pagina=${pagina}`);
    return this.http.get<CrmOportunidade[]>(`${this.url}/etapa/${etapaId}/cartoes?${params.join('&')}`);
  }

  /**
   * Indicadores do funil no período, com o mesmo filtro de responsável do quadro.
   *
   * @param de  'YYYY-MM-DD'; sem ele a API usa 30 dias atrás
   * @param ate 'YYYY-MM-DD'; sem ele, hoje
   */
  resumo(funilId: number, de?: string, ate?: string, filtro?: string): Observable<CrmResumo> {
    const params = this.filtros(filtro);
    if (de) {
      params.push(`de=${de}`);
    }
    if (ate) {
      params.push(`ate=${ate}`);
    }
    const qs = params.length ? '?' + params.join('&') : '';
    return this.http.get<CrmResumo>(`${this.url}/funil/${funilId}/resumo${qs}`);
  }

  oportunidade(id: number): Observable<CrmOportunidade> {
    return this.http.get<CrmOportunidade>(`${this.url}/oportunidade/${id}`);
  }

  /** Limites do alerta de contato parado; nulo volta ao padrão. */
  definirAlertas(funilId: number, alertaAtencaoHoras: number | null, alertaCriticoHoras: number | null): Observable<CrmFunil> {
    return this.http.put<CrmFunil>(`${this.url}/funil/${funilId}/alertas`, { alertaAtencaoHoras, alertaCriticoHoras });
  }

  private filtros(filtro?: string, busca?: string): string[] {
    const params: string[] = [];
    if (filtro === 'meus' || filtro === 'sem') {
      params.push(`filtro=${filtro}`);
    } else if (filtro) {
      params.push(`responsavel=${encodeURIComponent(filtro)}`);
    }
    if (busca && busca.trim()) {
      params.push(`busca=${encodeURIComponent(busca.trim())}`);
    }
    return params;
  }

  /**
   * Parâmetros do CRM da filial da sessão.
   *
   * Não falha por falta de configuração: a API devolve os padrões quando nada foi gravado.
   */
  parametros(): Observable<CrmParametro> {
    return this.http.get<CrmParametro>(`${this.url}/parametro`);
  }

  salvarParametros(parametro: CrmParametro): Observable<CrmParametro> {
    return this.http.put<CrmParametro>(`${this.url}/parametro`, parametro);
  }

  /**
   * Faz as conversas seguirem o responsável do cartão.
   *
   * Acerto do histórico: antes o dono da conversa vinha de quem respondia primeiro e o do cartão de
   * outro caminho. Daqui em diante cada atribuição já propaga sozinha.
   */
  sincronizarConversas(): Observable<{ cartoes: number; conversas: number }> {
    return this.http.post<{ cartoes: number; conversas: number }>(`${this.url}/sincronizar-conversas`, {});
  }

  /**
   * Desfaz o vínculo de cartões com cliente cujo telefone não é dele.
   *
   * `simular = true` não grava nada: devolve a mesma contagem e alguns exemplos, para ver o tamanho
   * do estrago antes de mexer.
   */
  corrigirVinculos(simular: boolean): Observable<CrmCorrecaoVinculos> {
    return this.http.post<CrmCorrecaoVinculos>(
      `${this.url}/corrigir-vinculos?simular=${simular}`, {});
  }

  /** Grava a etapa. É por aqui que a transação restrita é definida ou removida (id nulo). */
  salvarEtapa(etapa: CrmFunilEtapa & { funilId?: number }): Observable<CrmFunilEtapa> {
    return this.http.put<CrmFunilEtapa>(`${this.url}/funil/etapa`, etapa);
  }

  etapas(funilId: number): Observable<CrmFunilEtapa[]> {
    return this.http.get<CrmFunilEtapa[]>(`${this.url}/funil/${funilId}/etapas`);
  }

  /**
   * Usuários da empresa, para escolher o responsável do cartão.
   *
   * O valor gravado é o login: é ele que casa com "meus cartões" e com a atribuição automática.
   */
  usuarios(): Observable<{ login: string; nome?: string; email?: string }[]> {
    return this.http.get<{ login: string; nome?: string; email?: string }[]>(`${this.url}/usuario`);
  }

  motivosPerda(): Observable<CrmMotivoPerda[]> {
    return this.http.get<CrmMotivoPerda[]>(`${this.url}/motivo-perda`);
  }

  salvar(oportunidade: CrmOportunidade): Observable<CrmOportunidade> {
    return this.http.put<CrmOportunidade>(`${this.url}/oportunidade`, oportunidade);
  }

  /** O arrastar: manda só o destino. */
  mover(id: number, etapaId: number, observacao?: string): Observable<CrmOportunidade> {
    return this.http.patch<CrmOportunidade>(`${this.url}/oportunidade/${id}/etapa`, { etapaId, observacao });
  }

  /** Aponta o cartão para um cadastro — o fecho do caminho manual. */
  ligarCadastro(id: number, cadastroId: number): Observable<CrmOportunidade> {
    return this.http.put<CrmOportunidade>(`${this.url}/oportunidade/${id}/cadastro`, { cadastroId });
  }

  /**
   * Cadastros que têm o telefone deste cartão — o caminho de ligar um cadastro feito à mão,
   * sem digitar o nome de novo.
   */
  cadastrosDoTelefone(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/oportunidade/${id}/cadastros-do-telefone`);
  }

  /**
   * Anota o que aconteceu numa passagem de etapa. Sem `historicoId`, anota a última.
   */
  anotarEtapa(id: number, observacao: string, historicoId?: number): Observable<CrmOportunidadeHist> {
    return this.http.put<CrmOportunidadeHist>(
      `${this.url}/oportunidade/${id}/etapa/observacao`, { observacao, historicoId });
  }

  ganhar(id: number, fatDoctoCControle?: number): Observable<CrmOportunidade> {
    return this.http.post<CrmOportunidade>(`${this.url}/oportunidade/${id}/ganhar`, { fatDoctoCControle });
  }

  /** @param etapaId coluna de encerramento onde o cartão foi solto; sem ela, a primeira ("Perdido"). */
  perder(id: number, motivoId: number, observacao?: string, etapaId?: number): Observable<CrmOportunidade> {
    return this.http.post<CrmOportunidade>(`${this.url}/oportunidade/${id}/perder`, { motivoId, observacao, etapaId });
  }

  /** Lançamentos que podem ser ligados ao cartão: os do cliente, ou os que batem com o número. */
  lancamentos(id: number, busca?: string): Observable<CrmLancamentoResumo[]> {
    const qs = busca ? `?busca=${encodeURIComponent(busca)}` : '';
    return this.http.get<CrmLancamentoResumo[]>(`${this.url}/oportunidade/${id}/lancamentos${qs}`);
  }

  /** Liga o lançamento ao cartão; `null` desfaz. Não muda a etapa. */
  vincularLancamento(id: number, controle: number | null): Observable<CrmOportunidade> {
    return this.http.put<CrmOportunidade>(`${this.url}/oportunidade/${id}/lancamento`, { controle });
  }

  /** Quem clicou vira o responsável do cartão. */
  assumir(id: number): Observable<CrmOportunidade> {
    return this.http.post<CrmOportunidade>(`${this.url}/oportunidade/${id}/assumir`, {});
  }

  historico(id: number): Observable<CrmOportunidadeHist[]> {
    return this.http.get<CrmOportunidadeHist[]>(`${this.url}/oportunidade/${id}/historico`);
  }

  porCadastro(cadastroId: number): Observable<CrmOportunidade[]> {
    return this.http.get<CrmOportunidade[]>(`${this.url}/oportunidade?cadastroId=${cadastroId}`);
  }

  excluir(id: number): Observable<any> {
    return this.http.delete<any>(`${this.url}/oportunidade/${id}`);
  }
}
